import type { AdminVehicle, VehicleImageRef } from '@ecowheels/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Badge, Card, ConfirmButton, Input } from '@/components/ui';
import { vehicleTitle } from '@/lib/vehicle-labels';

/** The API decodes with `Buffer.from(base64,'base64')`, so strip the data-URI prefix. */
const readAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Photo management for one vehicle: upload, reorder by dragging, edit alt text,
 * delete.
 *
 * Alt text is auto-filled from the car's name on upload rather than demanded
 * up front — the post editor blocks submission until every image has one, which
 * is reasonable for two images and miserable for twenty. A sensible default
 * that is then editable in place gets accessible alt text onto every photo
 * without making bulk upload a chore.
 */
export function VehiclePhotos({ vehicle }: { vehicle: AdminVehicle }) {
  const queryClient = useQueryClient();
  const [order, setOrder] = useState<VehicleImageRef[]>(vehicle.images);
  const [dragging, setDragging] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  // Local copy so a drag reorders instantly; the server is told once, on drop.
  useEffect(() => {
    setOrder(vehicle.images);
  }, [vehicle.images]);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['vehicles', String(vehicle.id)] });
    queryClient.invalidateQueries({ queryKey: ['vehicles'] });
  };

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      // Sequential, not Promise.all: twenty photos at once is twenty parallel
      // multi-megabyte request bodies, and the progress line would be a lie.
      for (const [index, file] of files.entries()) {
        setProgress(`Feltöltés ${index + 1}/${files.length}…`);
        await api.post<AdminVehicle>(`/admin/vehicles/${vehicle.id}/images`, {
          name: file.name,
          base64: await readAsBase64(file),
          alt: `${vehicleTitle(vehicle)} – ${vehicle.images.length + index + 1}. kép`,
        });
      }
    },
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: ApiError) => setError(e.message),
    onSettled: () => setProgress(null),
  });

  const reorder = useMutation({
    mutationFn: (imageIds: number[]) =>
      api.post(`/admin/vehicles/${vehicle.id}/images/order`, { imageIds }),
    onSuccess: refresh,
    onError: (e: ApiError) => {
      setError(e.message);
      // Snap back to the server's order rather than leaving the screen showing
      // an arrangement that was never saved.
      refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (imageId: number) =>
      api.delete(`/admin/vehicles/${vehicle.id}/images/${imageId}`),
    onSuccess: refresh,
    onError: (e: ApiError) => setError(e.message),
  });

  const setAlt = useMutation({
    mutationFn: ({ imageId, alt }: { imageId: number; alt: string }) =>
      api.patch(`/admin/vehicles/${vehicle.id}/images/${imageId}`, { alt }),
    onSuccess: refresh,
    onError: (e: ApiError) => setError(e.message),
  });

  function drop(targetIndex: number) {
    if (dragging === null || dragging === targetIndex) return;
    const next = [...order];
    const [moved] = next.splice(dragging, 1);
    if (moved) next.splice(targetIndex, 0, moved);
    setOrder(next);
    setDragging(null);
    reorder.mutate(next.map((image) => image.id));
  }

  return (
    <Card className="mt-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-brand">
          Képek ({order.length})
        </h2>
        <label className="cursor-pointer">
          <input
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (files.length) upload.mutate(files);
            }}
          />
          <span className="inline-flex items-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-ink hover:opacity-90">
            {progress ?? 'Képek hozzáadása'}
          </span>
        </label>
      </div>

      {error && (
        <p role="alert" className="mt-3 text-sm text-red-400">
          {error}
        </p>
      )}

      {order.length === 0 ? (
        <p className="mt-4 text-sm text-white/40">
          Még nincs kép. Az első feltöltött kép lesz a borítókép.
        </p>
      ) : (
        <>
          <p className="mt-3 text-xs text-white/40">
            Húzza a képeket a sorrend módosításához. Az első kép a borítókép, ez jelenik meg a
            listában.
          </p>

          <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {order.map((image, index) => (
              <li
                key={image.id}
                draggable
                onDragStart={() => setDragging(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => drop(index)}
                className={`rounded-lg border border-white/10 bg-white/[0.02] ${
                  dragging === index ? 'opacity-40' : ''
                }`}
              >
                <div className="relative cursor-grab active:cursor-grabbing">
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="h-40 w-full rounded-t-lg bg-white/5 object-cover"
                    loading="lazy"
                  />
                  <span className="absolute left-2 top-2">
                    {index === 0 ? (
                      <Badge tone="success">Borítókép</Badge>
                    ) : (
                      <Badge tone="neutral">{index + 1}</Badge>
                    )}
                  </span>
                </div>

                <div className="space-y-2 p-2">
                  <Input
                    defaultValue={image.alt}
                    aria-label="Alt szöveg"
                    placeholder="Alt szöveg"
                    className="text-xs"
                    // Saved on blur rather than per keystroke: a PATCH per
                    // character would be dozens of requests per caption.
                    onBlur={(e) => {
                      const alt = e.target.value.trim();
                      if (alt && alt !== image.alt) setAlt.mutate({ imageId: image.id, alt });
                    }}
                  />
                  <ConfirmButton
                    onConfirm={() => remove.mutate(image.id)}
                    confirmText="Biztosan törli ezt a képet?"
                    className="w-full !px-2 !py-1 text-xs"
                  >
                    Kép törlése
                  </ConfirmButton>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}
